// Пороги ширины — ОДНО место на весь продукт.
//
// Числа отсюда читают и стили (плагин в `tailwind.config.ts`), и код (`./index.ts`). Если
// завести их дважды, они разъедутся: в одном месте поправят, в другом забудут, и получится
// ширина, при которой разметка уже перестроилась, а расчёт ещё нет. Ровно этой болезнью болеет
// высота строки сетки, и там об этом написано предупреждение в самом файле.
//
// ## Откуда взялись именно эти числа
//
// Планшет заказчика — iPad mini 7: 1133 x 744 точки. Считаем, что занято до содержимого:
// полоса разделов 240 плюс колонка списка 416 — это 656 точек, и на самое содержимое в
// портрете остаётся 88. То есть портрет требует показывать одну поверхность за раз, а не
// «сжать колонки»: сжимать нечего.
//
// В ландшафте того же планшета остаётся 477 — работать можно, но правая колонка режет
// заголовки величин («Неподтверждён…», «Долги сотрудни…»). Значит полоса разделов там обязана
// быть узкой: 72 точки вместо 240 возвращают содержимому 168.
//
// На рабочем столе ничего не меняется: 1440 и шире — это прежний кабинет один в один.

/**
 * Ниже этого кабинет не работает и говорит об этом прямо.
 *
 * Так выглядит планшет в режиме «поверх другого приложения»: полоса примерно в 375 точек. Из
 * них 72 занимает полоса разделов, и на список людей остаётся 300 — фамилия с меткой туда не
 * помещается, а ведомость не помещается тем более. Третья раскладка «всё в столбик» означала
 * бы вторую реализацию тех же экранов, которую правило о дублировании прямо запрещает; и
 * рисовать кашу вместо кабинета честнее, чем делать вид, что он работает.
 */
export const ПОРОГ_МАЛО = 480;

/** Ниже этого одна поверхность за раз: список ИЛИ карточка. Портрет планшета — 744. */
export const ПОРОГ_УЗКО = 900;

/**
 * С этого начинается прежняя раскладка рабочего стола: широкая полоса разделов, три колонки.
 *
 * Число выбрано так, чтобы НИ ОДИН компьютер не поменял вид. Ноутбук без монитора — это 1280,
 * и полоса разделов на нём остаётся прежней: 1280 − 240 − 416 = 624 точки содержимому, этого
 * хватает. Планшет в ландшафте — 1133 или 1194, и там те же 240 точек полосы уже режут
 * заголовки величин в сводке.
 *
 * Порог 1440 сюда не годился: под него попадал ровно ноутбук, то есть правка ради планшета
 * меняла бы вид тем, кто ни о каком планшете не просил.
 */
export const ПОРОГ_ШИРОКО = 1200;

/** Режим ширины. Между `узко` и `широко` — ландшафт планшета и маленькие окна на компьютере. */
export type Ширина = 'мало' | 'узко' | 'средне' | 'широко';

export function ширинаПоЧислу(точек: number): Ширина {
  if (точек < ПОРОГ_МАЛО) {
    return 'мало';
  }
  if (точек < ПОРОГ_УЗКО) {
    return 'узко';
  }
  return точек < ПОРОГ_ШИРОКО ? 'средне' : 'широко';
}
